import { useDraggable } from '@dnd-kit/core'
import { uniqueId } from 'lodash'
import { useEffect, useRef } from 'react'
import { Coordinates, Position, Transform } from 'types'
import { transformOriginMultiplier } from 'utils'
import CropScalerType from '../types/scaler'
import { Icon, Scaler } from './style'

type Props = {
    className?: string
    controlType: string
    crop: Position
    lockAspectRatio?: boolean
    type: CropScalerType
    onDragStateChange?: (dragging: boolean) => void
    onScale: (transform: Transform) => void
}

const CropScaler = ({
    className,
    controlType,
    crop,
    lockAspectRatio = false,
    type,
    onDragStateChange,
    onScale,
}: Props) => {
    const idRef = useRef<string>(uniqueId('crop-scaler'))
    const { attributes, isDragging, listeners, setNodeRef, transform } = useDraggable({
        id: idRef.current,
        data: { control: 'crop', controlId: idRef.current, controlType },
    })

    useEffect(() => {
        onDragStateChange?.(isDragging)
    }, [isDragging, onDragStateChange])

    useEffect(() => {
        if (!transform) return

        const { x, y } = transform
        const transformOrigin = CropScalerType.transformOrigin(type)
        const origin = transformOriginMultiplier(transformOrigin)

        const multiplier: Coordinates = {
            x: origin.x > 0 ? -origin.x : 1,
            y: origin.y > 0 ? -origin.y : 1,
        }

        const scaleX = Math.max(0, 1 + (x * multiplier.x) / crop.width)
        const scaleY = Math.max(0, 1 + (y * multiplier.y) / crop.height)
        const averageScale = (scaleX + scaleY) / 2

        onScale({
            origin: transformOrigin,
            rotation: 0,
            scaleX: lockAspectRatio ? averageScale : scaleX,
            scaleY: lockAspectRatio ? averageScale : scaleY,
            x: 0,
            y: 0,
        })
    }, [transform?.x, transform?.y])

    return (
        <Scaler {...listeners} {...attributes} className={className} ref={setNodeRef} type={type}>
            <Icon />
        </Scaler>
    )
}

export default CropScaler
